<script setup>
/** 成片的拼接顺序：开场 CG → 各场片段 → 结局 CG，下面是拼好的成片。 */
import { useI18n } from 'vue-i18n'

import { formatDuration, formatSize } from '../generation-records-format.js'
import { segmentStage } from './segment-stage.js'

defineProps({
  production: { type: Object, required: true },
  /** `[{ scene, segment }]`，按场次顺序。 */
  scenes: { type: Array, required: true },
  finalVideo: { type: Object, default: null },
  segmentCount: { type: Number, default: null },
})

const { t } = useI18n()
</script>

<template>
  <ol class="assembly-strip">
    <li v-if="production.opening" class="assembly-strip__cell">
      <video :src="production.opening.public_url" muted preload="metadata" />
      <span>{{ t('visualGameAdmin.generationRecords.process.assembly.opening') }}</span>
    </li>
    <li v-for="{ scene, segment } in scenes" :key="scene.scene_no" class="assembly-strip__cell">
      <video v-if="segment?.clip" :src="segment.clip.public_url" muted preload="metadata" />
      <span v-else class="assembly-strip__placeholder">{{ t(`visualGameAdmin.generationRecords.process.scene.stage.${segmentStage(segment)}`) }}</span>
      <span>{{ t('visualGameAdmin.generationRecords.process.scene.scene', { scene: scene.scene_no }) }} · {{ scene.duration }}s</span>
    </li>
    <li v-if="production.ending" class="assembly-strip__cell">
      <video :src="production.ending.public_url" muted preload="metadata" />
      <span>{{ t('visualGameAdmin.generationRecords.process.assembly.ending') }}</span>
    </li>
  </ol>

  <div v-if="finalVideo" class="assembly-strip__final">
    <video :src="finalVideo.public_url" controls preload="metadata" />
    <div class="assembly-strip__final-meta">
      <span>
        {{ formatDuration(finalVideo.duration_ms) }} · {{ formatSize(finalVideo.size) }}
        <template v-if="segmentCount !== null">· {{ t('visualGameAdmin.generationRecords.process.assembly.segments', { count: segmentCount }) }}</template>
      </span>
      <a :href="finalVideo.public_url" target="_blank" rel="noreferrer">{{ t('visualGameAdmin.generationRecords.process.assembly.open') }}</a>
    </div>
  </div>
</template>

<style scoped>
.assembly-strip {
  display: flex;
  gap: var(--drama-space-lg);
  margin: 0;
  padding: 0 0 var(--drama-space-sm);
  overflow-x: auto;
  list-style: none;
}

.assembly-strip__cell {
  position: relative;
  display: flex;
  flex: 0 0 200px;
  flex-direction: column;
  gap: var(--drama-space-xs);
  font-size: var(--drama-font-size-xs);
}

.assembly-strip__cell + .assembly-strip__cell::before {
  content: '→';
  position: absolute;
  top: 44px;
  left: calc(-1 * var(--drama-space-lg));
  width: var(--drama-space-lg);
  color: var(--drama-color-text-muted);
  text-align: center;
}

.assembly-strip__cell video,
.assembly-strip__placeholder {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-bg);
}

.assembly-strip__placeholder {
  display: grid;
  place-items: center;
  border: 1px dashed var(--drama-color-border-strong);
  color: var(--drama-color-text-muted);
}

.assembly-strip__cell > span:last-child {
  color: var(--drama-color-text-muted);
}

.assembly-strip__final {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-sm);
  max-width: 720px;
  margin-top: var(--drama-space-xl);
}

.assembly-strip__final video {
  width: 100%;
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-bg);
}

.assembly-strip__final-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  font-size: var(--drama-font-size-sm);
}

.assembly-strip__final-meta a {
  color: var(--drama-color-primary);
}
</style>
